import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import "./UserAvatar.css";

const UserAvatar = ({ user, size = "medium", onClick, className = "" }) => {
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadEmployeeAvatar = async () => {
      setLoading(true);
      setImageError(false);

      // First, try to get headshot from employees table by matching email
      // Prefer optimized 320x320 WebP version, fall back to original
      if (user?.email) {
        try {
          const { data: employee, error } = await supabase
            .from("employees")
            .select("headshot_optimized_url, headshot_url")
            .eq("email", user.email)
            .single();

          if (
            !error &&
            (employee?.headshot_optimized_url || employee?.headshot_url)
          ) {
            setAvatarUrl(
              employee.headshot_optimized_url || employee.headshot_url,
            );
            setLoading(false);
            return;
          }
        } catch (err) {
          // Silently fail - will fall back to user metadata
        }
      }

      // Fall back to user metadata avatar_url
      if (user?.user_metadata?.avatar_url) {
        loadAvatar(user.user_metadata.avatar_url);
      } else {
        setLoading(false);
      }
    };

    loadEmployeeAvatar();
  }, [user]);

  const loadAvatar = async (url) => {
    try {
      // URL is already a full URL (either http or a public URL from storage)
      if (url.startsWith("http")) {
        setAvatarUrl(url);
      } else {
        // Legacy format - convert path to public URL
        const { data } = supabase.storage.from("avatars").getPublicUrl(url);

        if (data?.publicUrl) {
          setAvatarUrl(data.publicUrl);
        } else {
          setAvatarUrl(null);
        }
      }
    } catch (error) {
      setAvatarUrl(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(null);
  };

  const getInitials = () => {
    if (user?.user_metadata?.full_name) {
      const names = user.user_metadata.full_name.split(" ");
      return names
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return "U";
  };

  const getBackgroundColor = () => {
    if (!user?.email) return "#3b82f6";
    const hash = user.email.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const colors = [
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#06b6d4",
      "#6366f1",
      "#84cc16",
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const sizeClasses = {
    small: "avatar-small",
    medium: "avatar-medium",
    large: "avatar-large",
  };

  return (
    <div
      className={`user-avatar ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      style={{
        backgroundColor: !avatarUrl ? getBackgroundColor() : "transparent",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {loading ? (
        <div className="avatar-loading"></div>
      ) : avatarUrl && !imageError ? (
        <img
          src={avatarUrl}
          alt="User avatar"
          className="avatar-image"
          onError={handleImageError}
        />
      ) : (
        <span className="avatar-initials">{getInitials()}</span>
      )}
    </div>
  );
};

export default UserAvatar;
